import { FC } from 'react';
import { EventScoreDto, ParticipantDto, ScoreDisplayType } from '../../api/sbapi/sbapi-api-model';
import { EventNameLabel, ScoreGrid, WrapperLink } from './sportsbook-event-scoreboard-layout';
import { ParticipantVenueRole } from '../../sportsbook-types';
import {
	isTeamLeading,
	isTeamLeadingOrTied,
	splitPeriodsToOngoingAndFinished
} from './sportsbook-event-scoreboard-utils';
import ScoreLine from './sportsbook-event-scoreboard-score-line';

type Props = {
	className?: string;
	eventName: string;
	eventScore: EventScoreDto;
	participants?: ParticipantDto[];
	isLive: boolean;
	dataAt?: string;
	size?: 'small' | 'large';
	displayType: ScoreDisplayType;
	linkToEvent: string;
	hasReversedOrder?: boolean;
	onClick?: () => void;
};

const SportsbookEventScoreboard: FC<Props> = ({
	className,
	eventName,
	eventScore,
	participants,
	isLive,
	dataAt,
	size,
	displayType,
	linkToEvent,
	hasReversedOrder,
	onClick
}) => {
	const homeParticipant = participants?.find(p => p.venueRole === ParticipantVenueRole.HOME);
	const awayParticipant = participants?.find(p => p.venueRole === ParticipantVenueRole.AWAY);
	const { homeScore, awayScore } = eventScore;
	const homeRow = hasReversedOrder ? 2 : 1;
	const awayRow = hasReversedOrder ? 1 : 2;

	if (!isLive) {
		return (
			<WrapperLink href={linkToEvent} className={className} data-at={dataAt} onClick={onClick}>
				{homeParticipant && awayParticipant ? (
					<ScoreGrid columnCount={2}>
						<ScoreLine row={homeRow} participant={homeParticipant} size={size} />
						<ScoreLine row={awayRow} participant={awayParticipant} size={size} />
					</ScoreGrid>
				) : (
					<EventNameLabel>{eventName}</EventNameLabel>
				)}
			</WrapperLink>
		);
	}

	if (displayType === ScoreDisplayType.TOTAL) {
		return (
			<WrapperLink href={linkToEvent} className={className} data-at={dataAt} onClick={onClick}>
				<ScoreGrid columnCount={2}>
					{homeParticipant && awayParticipant ? (
						<>
							<ScoreLine
								row={homeRow}
								participant={homeParticipant}
								score={homeScore.score}
								size={size}
								isParticipantNameHighlighted={isTeamLeading(homeScore.score, awayScore.score)}
							/>
							<ScoreLine
								row={awayRow}
								participant={awayParticipant}
								score={awayScore.score}
								size={size}
								isParticipantNameHighlighted={isTeamLeading(awayScore.score, homeScore.score)}
							/>
						</>
					) : (
						<EventNameLabel>{eventName}</EventNameLabel>
					)}
				</ScoreGrid>
			</WrapperLink>
		);
	}

	if (displayType === ScoreDisplayType.PERIOD) {
		const { ongoingPeriod: homeOngoingPeriod, finishedPeriods: homeFinishedPeriods } =
			splitPeriodsToOngoingAndFinished(homeScore.periodScores);
		const { ongoingPeriod: awayOngoingPeriod, finishedPeriods: awayFinishedPeriods } =
			splitPeriodsToOngoingAndFinished(awayScore.periodScores);
		const columnCount = homeScore.periodScores.length + 1 + (homeOngoingPeriod?.secondaryScore != null ? 1 : 0);

		return (
			<WrapperLink href={linkToEvent} className={className} data-at={dataAt} onClick={onClick}>
				<ScoreGrid columnCount={columnCount} isDisplayingSetScores>
					{homeParticipant && awayParticipant ? (
						<>
							<ScoreLine
								row={homeRow}
								participant={homeParticipant}
								ongoingPeriod={homeOngoingPeriod}
								finishedPeriods={homeFinishedPeriods}
								size={size}
								isSecondaryScoreHighlighted={isTeamLeadingOrTied(
									homeOngoingPeriod?.secondaryScore ?? null,
									awayOngoingPeriod?.secondaryScore ?? null
								)}
							/>
							<ScoreLine
								row={awayRow}
								participant={awayParticipant}
								ongoingPeriod={awayOngoingPeriod}
								finishedPeriods={awayFinishedPeriods}
								size={size}
								isSecondaryScoreHighlighted={isTeamLeadingOrTied(
									awayOngoingPeriod?.secondaryScore ?? null,
									homeOngoingPeriod?.secondaryScore ?? null
								)}
							/>
						</>
					) : (
						<EventNameLabel>{eventName}</EventNameLabel>
					)}
				</ScoreGrid>
			</WrapperLink>
		);
	}

	return (
		<WrapperLink href={linkToEvent} className={className} data-at={dataAt} onClick={onClick}>
			<EventNameLabel>{eventName}</EventNameLabel>
		</WrapperLink>
	);
};

export default SportsbookEventScoreboard;
